/**
 * 本から TODO を作るフォーマットと、本ごとの作成済み TODO のストア。
 *
 * - templates: ユーザーが登録したフォーマット（並び順どおり）
 * - existingTitles: ISBN → その本から作った未完了 TODO のタイトル。同じタイトルを「追加済み」にするのに使う
 * - projects: フォーマットに付けられるプロジェクトの候補（TODO に付いているもの）
 */

import { create } from 'zustand'

import { listTodoProjects, listTodos } from '@/api/services/todo'
import {
  createTodoBookTemplate,
  deleteTodoBookTemplate,
  listTodoBookTemplates,
  reorderTodoBookTemplates,
  updateTodoBookTemplate,
} from '@/api/services/todo-book-template'
import type { TodoResponse } from '@/api/types/todo'
import type { TodoBookTemplate, TodoBookTemplateInput } from '@/api/types/todo-book-template'

import { useTodoStore } from './useTodoStore'

export type BookTodoPayload = {
  title: string
  isbn: string | null
  project: string | null
}

type BookTodoTemplateState = {
  templates: TodoBookTemplate[]
  isLoaded: boolean
  existingTitles: Map<string, string[]>
  projects: string[]
  loadProjects: () => Promise<void>
  loadTemplates: () => Promise<void>
  createTemplate: (input: TodoBookTemplateInput) => Promise<TodoBookTemplate>
  updateTemplate: (id: number, patch: Partial<TodoBookTemplateInput>) => Promise<void>
  deleteTemplate: (id: number) => Promise<void>
  moveTemplate: (id: number, delta: -1 | 1) => Promise<void>
  loadExistingTitles: (isbn: string) => Promise<void>
  addBookTodo: (payload: BookTodoPayload) => Promise<TodoResponse>
}

let loadingTemplates: Promise<void> | null = null

export const useBookTodoTemplateStore = create<BookTodoTemplateState>((set, get) => ({
  templates: [],
  isLoaded: false,
  existingTitles: new Map(),
  projects: [],

  loadProjects: async () => {
    set({ projects: await listTodoProjects() })
  },

  loadTemplates: async () => {
    if (get().isLoaded) return
    if (loadingTemplates) return loadingTemplates
    loadingTemplates = listTodoBookTemplates()
      .then((templates) => set({ templates, isLoaded: true }))
      .finally(() => {
        loadingTemplates = null
      })
    return loadingTemplates
  },

  createTemplate: async (input) => {
    const created = await createTodoBookTemplate(input)
    set({ templates: [...get().templates, created] })
    return created
  },

  updateTemplate: async (id, patch) => {
    const updated = await updateTodoBookTemplate(id, patch)
    set({ templates: get().templates.map((t) => (t.id === id ? updated : t)) })
  },

  deleteTemplate: async (id) => {
    await deleteTodoBookTemplate(id)
    set({ templates: get().templates.filter((t) => t.id !== id) })
  },

  moveTemplate: async (id, delta) => {
    const ids = get().templates.map((t) => t.id)
    const from = ids.indexOf(id)
    const to = from + delta
    if (from < 0 || to < 0 || to >= ids.length) return
    const next = [...ids]
    next.splice(from, 1)
    next.splice(to, 0, id)
    const reordered = await reorderTodoBookTemplates(next)
    set({ templates: reordered })
  },

  loadExistingTitles: async (isbn) => {
    const todos = await listTodos({ source: 'library_book', source_ref: isbn })
    const next = new Map(get().existingTitles)
    next.set(
      isbn,
      todos.map((t) => t.title),
    )
    set({ existingTitles: next })
  },

  addBookTodo: async ({ title, isbn, project }) => {
    const created = await useTodoStore.getState().addTodo({
      title,
      source: isbn ? 'library_book' : 'manual',
      source_ref: isbn,
      project,
    })
    if (isbn) {
      const next = new Map(get().existingTitles)
      next.set(isbn, [...(next.get(isbn) ?? []), created.title])
      set({ existingTitles: next })
    }
    return created
  },
}))
